import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Wallet, X } from 'lucide-react';

import { lotPaymentsAPI } from '../../lib/lotPaymentsApi';
import { STATUTS_LOT } from '../../lib/lotsFiltres';
import { symboleDevise } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { messageErreur } from '../../lib/erreurs';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/sonner';
import { usePagination } from '../../hooks/usePagination';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { useTransporteursLots } from '../../hooks/useTransporteursLots';
import { PageHeader } from '../../components/ui/page-header';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  CelluleEntier,
  CelluleMontant,
  StatutPaiement,
  TuilesSituation,
  statMontant,
} from '../../components/lots/paiementsUi';

const CADRE = 'mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8';
const TOUS = 'tous';
const VUES = [
  ['lots', 'Par lot'],
  ['partenaires', 'Par partenaire'],
];

/**
 * Paiements : la situation de paiement de tous les lots, par lot ou par
 * partenaire, avec les totaux par devise. Réservé à qui voit les montants.
 */
const LotsPaiementsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { voitArgent } = useDroitsLots();
  const { options: societes } = useTransporteursLots();

  const vue = searchParams.get('vue') === 'partenaires' ? 'partenaires' : 'lots';
  const statut = STATUTS_LOT.includes((searchParams.get('statut') || '').toUpperCase())
    ? searchParams.get('statut').toUpperCase()
    : '';
  const transporteur = (searchParams.get('transporteur') || '').trim();

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);

  const changer = useCallback(
    (changements) =>
      setSearchParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          Object.entries(changements).forEach(([cle, valeur]) => {
            if (!valeur) suivant.delete(cle);
            else suivant.set(cle, valeur);
          });
          suivant.delete('lots-page');
          suivant.delete('partenaires-page');
          return suivant;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setChargement(true);
      try {
        setDonnees(await lotPaymentsAPI.summary({ status: statut, transporter_id: transporteur }));
        setErreur(false);
      } catch (err) {
        console.error('Failed to load lot payments summary:', err);
        setErreur(true);
        if (!silencieux) toast.error(messageErreur(err, 'Impossible de charger les paiements'));
      } finally {
        setChargement(false);
      }
    },
    [statut, transporteur],
  );

  useEffect(() => {
    if (voitArgent) charger();
  }, [charger, voitArgent]);

  useRafraichirSur(['lots', 'finance'], charger, { actif: voitArgent });

  const lots = useMemo(() => donnees?.lots || [], [donnees]);
  const partenaires = useMemo(() => donnees?.partners || [], [donnees]);
  const totaux = useMemo(() => donnees?.totals || [], [donnees]);

  const reinitialiser = `${statut}|${transporteur}`;
  const paginationLots = usePagination(lots, { cle: 'lots', reinitialiser });
  const paginationPartenaires = usePagination(partenaires, { cle: 'partenaires', reinitialiser });

  const optionsTransporteurs = useMemo(
    () => [{ value: '', label: 'Tous les transporteurs' }, ...societes],
    [societes],
  );

  const filtresActifs = [statut, transporteur].filter(Boolean).length;

  if (!voitArgent) {
    return (
      <div className={CADRE} data-testid="lots-paiements">
        <PageHeader eyebrow="Lots attribués" title="Paiements" />
        <EmptyState variant="error" icon={Lock} title="Ces montants sont réservés à la direction" />
      </div>
    );
  }

  // Les chiffres de l'en-tête : seulement quand une seule devise les rend sans ambiguïté.
  const unique = totaux.length === 1 ? totaux[0] : null;
  const stats = [
    { label: filtresActifs ? 'Lots trouvés' : 'Lots', valeur: lots.length, testid: 'paiements-stat-lots' },
    ...(unique
      ? [
          statMontant({
            label: 'Reste dû',
            valeur: unique.remaining_due,
            devise: unique.currency,
            ton: Number(unique.remaining_due) > 0 ? 'attention' : 'positif',
            testid: 'paiements-stat-reste',
          }),
          statMontant({
            label: 'Net dû',
            valeur: unique.net_due,
            devise: unique.currency,
            testid: 'paiements-stat-net',
          }),
        ]
      : []),
  ];

  const versLot = (lotId) => navigate(`/lots/${lotId}/paiements`);

  return (
    <div className={CADRE} data-testid="lots-paiements">
      <PageHeader eyebrow="Lots attribués" title="Paiements" stats={stats} />

      <div className="flex flex-wrap items-center gap-2" data-testid="paiements-filtres">
        <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label="Affichage">
          {VUES.map(([valeur, texte]) => (
            <button
              key={valeur}
              type="button"
              onClick={() => changer({ vue: valeur === 'lots' ? '' : valeur })}
              aria-pressed={vue === valeur}
              className={cn(
                'h-7 rounded-md px-2.5 text-xs font-semibold transition-colors',
                vue === valeur ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`paiements-vue-${valeur}`}
            >
              {texte}
            </button>
          ))}
        </div>
        <Select value={statut || TOUS} onValueChange={(valeur) => changer({ statut: valeur === TOUS ? '' : valeur })}>
          <SelectTrigger className="input-base w-40" aria-label="État du lot" data-testid="paiements-filtre-statut">
            <SelectValue placeholder="État du lot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>État : tous</SelectItem>
            {STATUTS_LOT.map((code) => (
              <SelectItem key={code} value={code}>
                {libelle('lot_statut', code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SearchableSelect
          className="input-base w-52"
          data-testid="paiements-filtre-transporteur"
          aria-label="Transporteur"
          placeholder="Transporteur"
          searchPlaceholder="Rechercher un transporteur…"
          emptyMessage="Aucun transporteur trouvé"
          value={transporteur}
          onValueChange={(valeur) => changer({ transporteur: valeur })}
          options={optionsTransporteurs}
        />
        {filtresActifs > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => changer({ statut: '', transporteur: '' })}
            data-testid="paiements-effacer-filtres"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Effacer les filtres
          </Button>
        ) : null}
      </div>

      {chargement && !donnees ? (
        <LoadingState shape="table" />
      ) : erreur && !donnees ? (
        <EmptyState
          variant="error"
          title="Les paiements n’ont pas pu être chargés"
          actionLabel="Réessayer"
          onAction={() => charger()}
        />
      ) : (
        <>
          {totaux.length > 0 ? (
            <section className="space-y-2" aria-label="Totaux" data-testid="paiements-totaux">
              {totaux.map((situation) => (
                <div key={situation.currency} className="space-y-1.5">
                  {totaux.length > 1 ? (
                    <h2 className="text-sm font-semibold text-foreground">{symboleDevise(situation.currency)}</h2>
                  ) : null}
                  <TuilesSituation situation={situation} testid={`paiements-tuiles-${situation.currency}`} />
                </div>
              ))}
            </section>
          ) : null}

          {lots.length === 0 ? (
            <EmptyState
              variant={filtresActifs ? 'filtered' : 'empty'}
              icon={Wallet}
              title={filtresActifs ? 'Aucun résultat' : 'Aucun lot à régler'}
              actionLabel={filtresActifs ? 'Effacer les filtres' : undefined}
              onAction={filtresActifs ? () => changer({ statut: '', transporteur: '' }) : undefined}
            />
          ) : vue === 'lots' ? (
            <>
              <Card className="card-base">
                <CardContent className="p-0">
                  <Table compact data-testid="paiements-table-lots">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lot</TableHead>
                        <TableHead>Transporteur</TableHead>
                        <TableHead className="text-right">Confirmés</TableHead>
                        <TableHead className="text-right">Dépotés</TableHead>
                        <TableHead className="text-right">Décomptés</TableHead>
                        <TableHead className="text-right">Valeur confirmée</TableHead>
                        <TableHead className="text-right">Décompté</TableHead>
                        <TableHead className="text-right">Acomptes</TableHead>
                        <TableHead className="text-right">Reste dû</TableHead>
                        <TableHead>Paiement</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginationLots.lignes.map((lot) => {
                        const situation = lot.situation || {};
                        const devise = lot.currency || situation.currency || 'XOF';
                        return (
                          <TableRow
                            key={lot.id}
                            className="cursor-pointer"
                            onClick={() => versLot(lot.id)}
                            data-testid={`paiements-lot-${lot.id}`}
                          >
                            <TableCell className="whitespace-nowrap font-mono font-semibold">
                              <Link
                                to={`/lots/${lot.id}/paiements`}
                                onClick={(evenement) => evenement.stopPropagation()}
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                {lot.reference || 'Lot'}
                              </Link>
                            </TableCell>
                            <TableCell>{lot.transporter_name || '—'}</TableCell>
                            <CelluleEntier valeur={situation.confirmed_trucks} />
                            <CelluleEntier valeur={situation.unloaded_trucks} />
                            <CelluleEntier valeur={situation.settled_trucks} />
                            <CelluleMontant valeur={situation.confirmed_value} devise={devise} />
                            <CelluleMontant valeur={situation.settled_amount} devise={devise} />
                            <CelluleMontant valeur={situation.advances_paid} devise={devise} />
                            <CelluleMontant valeur={situation.remaining_due} devise={devise} gras />
                            <TableCell>
                              <StatutPaiement code={situation.payment_status} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <BarrePagination pagination={paginationLots} nom="lots" />
            </>
          ) : (
            <>
              <Card className="card-base">
                <CardContent className="p-0">
                  <Table compact data-testid="paiements-table-partenaires">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Transporteur</TableHead>
                        <TableHead className="text-right">Lots</TableHead>
                        <TableHead className="text-right">Confirmés</TableHead>
                        <TableHead className="text-right">Dépotés</TableHead>
                        <TableHead className="text-right">Décomptés</TableHead>
                        <TableHead className="text-right">Valeur confirmée</TableHead>
                        <TableHead className="text-right">Décompté</TableHead>
                        <TableHead className="text-right">Acomptes</TableHead>
                        <TableHead className="text-right">Reste dû</TableHead>
                        <TableHead className="text-right">Net dû</TableHead>
                        <TableHead>Paiement</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginationPartenaires.lignes.map((partenaire) => {
                        const situation = partenaire.situation || {};
                        const devise = situation.currency || 'XOF';
                        const cle = `${partenaire.supplier_party_id || partenaire.transporter_company_id || partenaire.transporter_name}|${devise}`;
                        const filtrable = Boolean(partenaire.transporter_company_id);
                        return (
                          <TableRow
                            key={cle}
                            className={filtrable ? 'cursor-pointer' : undefined}
                            onClick={
                              filtrable
                                ? () => changer({ vue: '', transporteur: partenaire.transporter_company_id })
                                : undefined
                            }
                            data-testid={`paiements-partenaire-${partenaire.transporter_company_id || partenaire.supplier_party_id}`}
                          >
                            <TableCell className="font-semibold">{partenaire.transporter_name || '—'}</TableCell>
                            <CelluleEntier valeur={partenaire.lot_count} />
                            <CelluleEntier valeur={situation.confirmed_trucks} />
                            <CelluleEntier valeur={situation.unloaded_trucks} />
                            <CelluleEntier valeur={situation.settled_trucks} />
                            <CelluleMontant valeur={situation.confirmed_value} devise={devise} />
                            <CelluleMontant valeur={situation.settled_amount} devise={devise} />
                            <CelluleMontant valeur={situation.advances_paid} devise={devise} />
                            <CelluleMontant valeur={situation.remaining_due} devise={devise} gras />
                            <CelluleMontant valeur={situation.net_due} devise={devise} />
                            <TableCell>
                              <StatutPaiement code={situation.payment_status} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <BarrePagination pagination={paginationPartenaires} nom="partenaires" />
            </>
          )}
        </>
      )}

      {erreur && donnees ? (
        <p className="text-xs text-destructive" role="alert">
          Les chiffres n’ont pas pu être actualisés.
        </p>
      ) : null}
    </div>
  );
};

export default LotsPaiementsPage;
